// DataSourceIndicator - Shows API status, source, and event count
// ALWAYS visible - provides hard proof of which API and source is being used
import React from 'react';
import { Database, Server, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { getApiBaseUrl, getDataSource, detectEnvironment } from '@/lib/api/config';
import { didNeonFail, getNeonErrorMessage } from '@/lib/api/events';

export function DataSourceIndicator({ eventCount = null, loading = false, error = null }) {
  const apiBaseUrl = getApiBaseUrl();
  const source = getDataSource();
  const environment = detectEnvironment();
  const neonFailed = didNeonFail();
  const errorMessage = getNeonErrorMessage();
  
  // Determine badge color
  const getBadgeStyle = () => {
    if (error || neonFailed) {
      return 'bg-red-500/90 text-white border-red-600';
    }
    if (source === 'neon') {
      return 'bg-green-500/90 text-white border-green-600';
    }
    return 'bg-blue-500/90 text-white border-blue-600';
  };
  
  return (
    <>
      {/* Main Indicator Badge - ALWAYS VISIBLE */}
      <div 
        className={`fixed top-2 right-2 z-50 px-3 py-2 rounded-lg text-xs font-mono shadow-lg border ${getBadgeStyle()}`}
        data-testid="api-status-indicator"
      >
        <div className="flex flex-col gap-1">
          {/* API URL */}
          <div className="flex items-center gap-1.5">
            <span className="opacity-70">API:</span>
            <span className="font-semibold truncate max-w-[200px]">
              {apiBaseUrl.replace('https://', '').replace('/api', '')}
            </span>
          </div>
          
          {/* Source */}
          <div className="flex items-center gap-1.5">
            {source === 'neon' ? (
              <Database className="w-3 h-3" />
            ) : (
              <Server className="w-3 h-3" />
            )}
            <span className="opacity-70">Source:</span>
            <span className="font-bold uppercase">{source}</span>
          </div>
          
          {/* Event Count */}
          <div className="flex items-center gap-1.5">
            {loading ? (
              <span className="opacity-70">Events: loading...</span>
            ) : error ? (
              <>
                <XCircle className="w-3 h-3" />
                <span className="opacity-70">Events:</span>
                <span className="font-bold">ERROR</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-3 h-3" />
                <span className="opacity-70">Events:</span>
                <span className="font-bold">{eventCount !== null ? eventCount : '?'}</span>
              </>
            )}
          </div>
          
          {/* Environment tag */}
          <div className="text-[10px] opacity-60 uppercase tracking-wider mt-0.5">
            {environment}
          </div>
        </div>
      </div>
      
      {/* Error Banner - Shows when API fails */}
      {(error || neonFailed) && (
        <div 
          className="fixed top-20 left-0 right-0 z-40 bg-red-600 text-white px-4 py-3"
          data-testid="api-error-banner"
        >
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <span className="font-semibold">API Error: </span>
              <span>{error || errorMessage || 'Failed to fetch data'}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DataSourceIndicator;
